/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IIconNameItem, IIconNameResolver, IIconPackItem } from "@adapptio/lib-blueprint";
import { createEventEmitter, emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";
import { ResourceService } from "../../resources/ResourceService";

/**
 * Icon name Resolver
 */
export class IconNameResolver implements IIconNameResolver {
	/** Invalidate event */
	public onInvalidate = createEventEmitter<void>();

	private invalidateHandler: () => void;

	/**
	 * Constructor
	 */
	public constructor(private resourceSvc: ResourceService) {
		this.invalidateHandler = () => {
			emitEvent(this.onInvalidate);
		};

		onEvent(this.resourceSvc.onIconPacksChanged, this.invalidateHandler);
	}

	/**
	 * Returns a list of available icons
	 */
	public getIconList(iconPackName?: string): IIconNameItem[] {
		if (iconPackName) {
			const iconPack = this.resourceSvc.getIconPackByName(iconPackName);

			if (iconPack) {
				return iconPack.icons;
			} else {
				return [];
			}
		} else {
			let icons = [];
			const iconPacks = this.resourceSvc.getIconPacks();

			for (let i = 0; i < iconPacks.length; i++) {
				icons = icons.concat(iconPacks[i].icons);
			}

			return icons;
		}
	}

	/**
	 * Returns a list of available icon packs
	 */
	public getIconPackList(): IIconPackItem[] {
		return this.resourceSvc.getIconPacks().map((item) => ({
			name: item.name,
			label: item.label,
			shortLabel: item.shortLabel,
			description: item.description
		}));
	}

	/**
	 * Disposes resolver
	 */
	public dispose(): void {
		offEvent(this.resourceSvc.onIconPacksChanged, this.invalidateHandler);
	}
}
